import React, { useEffect } from 'react';
import { useLatest } from 'react-use';
import { createActor, fromPromise } from 'xstate';

import { useNamedLogger } from '@eluve/logger';

import { fileSyncTriggerStore } from './fileSyncTriggerStore';
import { periodicLocalFileScanMachine } from './periodicLocalFileScan.machine';
import { AppointmentFilesTableDependency, GetFileReferences } from './types';

export interface PeriodicFileSyncMachineProviderProps {
  appointmentFilesTable: AppointmentFilesTableDependency;
  getFileReferences: () => Promise<GetFileReferences>;
}

export const PeriodicLocalFileScanMachineProvider: React.FC<
  PeriodicFileSyncMachineProviderProps
> = ({ appointmentFilesTable, getFileReferences }) => {
  const logger = useNamedLogger('PeriodicLocalFileScanMachineProvider');

  const latestAppointmentFilesTable = useLatest(appointmentFilesTable);
  const latestGetFileReferences = useLatest(getFileReferences);

  useEffect(() => {
    const machineActor = createActor(
      periodicLocalFileScanMachine.provide({
        actors: {
          getFiles: fromPromise<GetFileReferences>(
            latestGetFileReferences.current,
          ),
        },
      }),
      {
        input: {
          appointmentFilesTable: latestAppointmentFilesTable.current,
          logger,
          checkForFileDriftIntervalMs: 1000 * 20,
        },
      },
    );

    machineActor.start();

    // Let completion (or anything else) force an immediate scan instead of
    // waiting for the next interval. [ENG-4717]
    let lastNonce =
      fileSyncTriggerStore.getSnapshot().context.scanRequestedNonce;
    const subscription = fileSyncTriggerStore.subscribe((snapshot) => {
      if (snapshot.context.scanRequestedNonce !== lastNonce) {
        lastNonce = snapshot.context.scanRequestedNonce;
        machineActor.send({ type: 'FORCE_SCAN' });
      }
    });

    return () => {
      logger.info('Stopping periodicFileSyncMachine');
      subscription.unsubscribe();
      machineActor.stop();
    };
  }, [latestAppointmentFilesTable, latestGetFileReferences, logger]);

  return null;
};
